import React, { useState, useEffect, useRef } from 'react';
import { uploadShopCoverPhoto, validateImageFile } from '../../utils/imageUpload';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';
import { Image } from 'react-iconly';

const ACCENT_GRAY = '#757575';
const ERROR_RED = '#F44336';

export default function VisualsStoryStep({ data, onNext, onBack, isFirst, isLast }) {
  const [coverPhoto, setCoverPhoto] = useState(data.shopCoverPhoto || null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState(data.shopCoverPhotoUrl || '');
  const [description, setDescription] = useState(data.shopDescription || '');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});
  const fileInputRef = useRef();

  useEffect(() => {
    setError('');
    if (touched.description && !description.trim()) {
      setError('Shop description is required.');
    } else {
      setError('');
    }
  }, [description, touched]);

  function handleImageUpload(e) {
    const file = e.target.files[0];
    if (file) {
      try {
        validateImageFile(file);
        setCoverPhoto(file);
        setCoverPhotoUrl(URL.createObjectURL(file));
        setError('');
      } catch (error) {
        setError(error.message);
      }
    }
  }

  function handleRemovePhoto() {
    setCoverPhoto(null);
    setCoverPhotoUrl('');
  }

  function handleNext(e) {
    e.preventDefault();
    setTouched({ description: true });
    
    if (!description.trim()) {
      setError('Shop description is required.');
      return;
    }

    onNext({ 
      shopDescription: description.trim(),
      shopCoverPhoto: coverPhoto,
      shopCoverPhotoUrl: coverPhotoUrl
    });
  }

  return (
    <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>Tell Your Story</h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: '10px 0 0 0' }}>
          Help customers discover your shop with a great photo and description.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Cover Photo Upload */}
        <div>
          <label style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, display: 'block', marginBottom: 8 }}>
            Shop Photo (optional)
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {coverPhotoUrl ? (
              <div style={{ position: 'relative', width: '100%', maxWidth: 300 }}>
                <img 
                  src={coverPhotoUrl} 
                  alt="Shop cover preview" 
                  style={{ 
                    width: '100%', 
                    height: 150, 
                    objectFit: 'cover', 
                    borderRadius: 12,
                    border: '2px solid #eee'
                  }} 
                />
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Remove cover photo"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                style={{
                  width: '100%',
                  maxWidth: 300,
                  height: 150,
                  border: '2px dashed #ddd',
                  borderRadius: 12,
                  background: '#FAF9F7',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  transition: 'border 0.2s',
                }}
                aria-label="Upload cover photo"
              >
                <Image 
                  set="regular" 
                  size={24} 
                  primaryColor={ACCENT_GRAY}
                />
                <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY, textAlign: 'center' }}>
                  Click to upload photo
                </span>
                <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#999' }}>
                  JPG, PNG, GIF, WebP up to 5MB
                </span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              aria-label="Cover photo file input"
            />
          </div>
        </div>

        {/* Shop Description */}
        <div>
          <label htmlFor="description" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, display: 'block', marginBottom: 8 }}>
            Shop Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={description}
            onChange={e => { 
              setDescription(e.target.value); 
              setTouched(t => ({ ...t, description: true }));
            }}
            rows={4}
            placeholder="Tell customers about your shop, your story, and what makes your food special..."
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 10,
              border: `2px solid ${error && !description.trim() ? ERROR_RED : '#eee'}`,
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              fontWeight: 500,
              background: '#FAF9F7',
              color: '#222',
              resize: 'vertical',
              minHeight: 100,
              fontFamily: 'inherit',
            }}
            required
            aria-label="Shop description"
            aria-describedby="description-error"
          />
          <div id="description-error" style={{ minHeight: 22, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, marginTop: 4 }}>
            {error && <span style={{ color: ERROR_RED }}>{error}</span>}
            {description.length > 0 && (
              <span style={{ color: ACCENT_GRAY }}>
                {description.length} characters
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            style={{
              background: '#fff',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
              minWidth: 120,
              transition: 'all 0.2s',
            }}
            aria-label="Back"
          >
            Back
          </button>
        )}
        <button
          type="submit"
          disabled={!description.trim()}
          style={{
            background: description.trim() ? COLORS.PRIMARY_GRADIENT : '#E0CFC2',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
                          padding: '14px 32px',
            fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
            fontWeight: 600,
            cursor: description.trim() ? 'pointer' : 'not-allowed',
            minHeight: 44,
            minWidth: 120,
            boxShadow: '0 1px 4px #0001',
            transition: 'background 0.2s',
          }}
          aria-label="Next"
        >
          Next
        </button>
      </div>
    </form>
  );
} 